import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  useColorMode,
  Icon,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  Flex,
  AspectRatio,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Switch,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { CheckCircleIcon, StarIcon, LockIcon } from '@chakra-ui/icons';
import { FaUsers, FaGlobe, FaRocket, FaLink, FaCoins, FaFileUpload, FaNodeJs, FaIdCard } from 'react-icons/fa';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../brandConfig';

// Animated gradient keyframes
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
  50% { transform: scale(1.02); box-shadow: 0 0 30px 10px rgba(34, 197, 94, 0.2); }
`;

const PokerInABox = () => {
  const { colorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [paymentStep, setPaymentStep] = useState<'confirm' | 'processing' | 'success'>('confirm');
  const [selectedPackage, setSelectedPackage] = useState<'setup' | 'readonly' | 'fullnode'>('setup');

  // Form state
  const [formData, setFormData] = useState({
    clubName: '',
    domain: '',
    dexAddress: '0xE592427A0AEce92De3Edee1F18E0157C05861564',
    tokenAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    useIpfs: false,
    allowNftsAsAvatars: false,
    nftAddress: '',
    useOurNode: false,
    nodeUrl: '',
    nodeNftId: '',
    texasHoldem: true,
    omaha: false,
    haveAppropriateLicense: false,
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCsvFile(e.target.files[0]);
    }
  };

  const packages = {
    setup: { name: 'Poker in a Box Setup', price: 15000 },
    readonly: { name: 'Read-Only Node Access (2 Years)', price: 4800 },
    fullnode: { name: 'Full Node Ownership (Perpetual)', price: 52000 },
  };

  const handleBuyNow = (pkg: 'setup' | 'readonly' | 'fullnode') => {
    setSelectedPackage(pkg);
    setPaymentStep('confirm');
    onOpen();
  };

  const handleConfirmPayment = () => {
    setPaymentStep('processing');
    // Simulate payment processing
    setTimeout(() => {
      setPaymentStep('success');
    }, 3000);
  };

  const handleCloseModal = () => {
    onClose();
    setPaymentStep('confirm');
  };

  return (
    <Box minH="100vh" bg={getColor('background.main', colorMode)}>
      <NavbarWithCallToAction />

      {/* Hero Section with Animated Background */}
      <Box
        position="relative"
        overflow="hidden"
        py={20}
        bgGradient={
          colorMode === 'dark'
            ? 'linear(to-br, gray.900, purple.900, gray.900)'
            : 'linear(to-br, gray.50, purple.50, gray.50)'
        }
      >
        {/* Animated gradient overlay */}
        <Box
          position="absolute"
          inset="0"
          bgGradient="linear(45deg, rgba(139, 92, 246, 0.1), rgba(34, 197, 94, 0.1), rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))"
          bgSize="400% 400%"
          animation={`${gradientAnimation} 15s ease infinite`}
        />

        <Container maxW="5xl" position="relative" zIndex="1">
          <VStack spacing={8} textAlign="center">
            <Badge
              colorScheme="green"
              fontSize="md"
              px={4}
              py={2}
              borderRadius="full"
              textTransform="none"
            >
              Early Adopter Offer
            </Badge>

            <Heading
              as="h1"
              size="3xl"
              bgGradient="linear(to-r, purple.400, green.400, blue.400)"
              bgClip="text"
              fontWeight="extrabold"
              lineHeight="1.2"
            >
              Poker in a Box
            </Heading>

            <Text
              fontSize="2xl"
              color={getColor('text.secondary', colorMode)}
              maxW="3xl"
              fontWeight="medium"
            >
              Your complete club infrastructure solution.
              Launch your own decentralized poker club with full branding,
              blockchain integration, and enterprise-grade infrastructure.
            </Text>

            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button
                size="lg"
                px={10}
                py={7}
                fontSize="xl"
                fontWeight="bold"
                bgGradient="linear(to-r, green.400, green.500)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, green.500, green.600)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 20px 40px rgba(34, 197, 94, 0.4)',
                }}
                transition="all 0.3s"
                onClick={() => handleBuyNow('setup')}
                animation={`${pulseAnimation} 3s ease-in-out infinite`}
              >
                Get Started - $15,000 USDC
              </Button>
            </HStack>

            <Text fontSize="sm" color={getColor('text.secondary', colorMode)} opacity={0.8}>
              One-time setup fee • Full deployment included • Pay with USDC
            </Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="5xl" py={16}>
        <VStack spacing={16}>
          {/* What's Included Section */}
          <VStack spacing={8} w="full">
            <Heading
              size="xl"
              color={getColor('text.primary', colorMode)}
              textAlign="center"
            >
              Everything You Need to Launch
            </Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              {/* Deployment Scope Card */}
              <Box
                p={8}
                bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'white'}
                borderRadius="2xl"
                border="2px solid"
                borderColor="green.400"
                position="relative"
                overflow="hidden"
              >
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  h="4px"
                  bgGradient="linear(to-r, green.400, green.500)"
                />
                <VStack align="start" spacing={6}>
                  <HStack>
                    <Icon as={StarIcon} color="green.400" boxSize={6} />
                    <Heading size="md" color={getColor('text.primary', colorMode)}>
                      Initial Deployment & Setup
                    </Heading>
                  </HStack>

                  <Text
                    fontSize="3xl"
                    fontWeight="bold"
                    bgGradient="linear(to-r, green.400, green.500)"
                    bgClip="text"
                  >
                    $15,000 USDC
                  </Text>

                  <List spacing={4}>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="green.400" />
                      Full custom branding and color integration for gaming tables
                    </ListItem>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="green.400" />
                      Web-based user interface customization
                    </ListItem>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="green.400" />
                      Block52 base layer connection to primary gaming table
                    </ListItem>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="green.400" />
                      Transaction Facilitation Node wiring & configuration
                    </ListItem>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="green.400" />
                      Full front-end deployment and activation
                    </ListItem>
                  </List>

                  <Button
                    w="full"
                    size="lg"
                    bgGradient="linear(to-r, green.400, green.500)"
                    color="white"
                    _hover={{
                      bgGradient: 'linear(to-r, green.500, green.600)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 30px rgba(34, 197, 94, 0.3)',
                    }}
                    transition="all 0.3s"
                    onClick={() => handleBuyNow('setup')}
                  >
                    Buy Now
                  </Button>
                </VStack>
              </Box>

              {/* Ongoing Support Card */}
              <Box
                p={8}
                bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'white'}
                borderRadius="2xl"
                border="1px solid"
                borderColor={getColor('border.medium', colorMode)}
              >
                <VStack align="start" spacing={6}>
                  <HStack>
                    <Icon as={LockIcon} color="purple.400" boxSize={6} />
                    <Heading size="md" color={getColor('text.primary', colorMode)}>
                      Ongoing Operational Support
                    </Heading>
                  </HStack>

                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    color="purple.400"
                  >
                    Included with Setup
                  </Text>

                  <List spacing={4}>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="purple.400" />
                      1 Hour Professional DevOps per month
                    </ListItem>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="purple.400" />
                      Dedicated technical maintenance support
                    </ListItem>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="purple.400" />
                      Operational guidance and optimization
                    </ListItem>
                    <ListItem color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="purple.400" />
                      Priority issue resolution
                    </ListItem>
                  </List>

                  <Box
                    w="full"
                    p={4}
                    bg={colorMode === 'dark' ? 'rgba(139, 92, 246, 0.1)' : 'purple.50'}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="purple.200"
                  >
                    <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      Your dedicated DevOps support ensures smooth operation and quick resolution of any technical issues.
                    </Text>
                  </Box>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>

          {/* Configure Your Club Form Section */}
          <VStack spacing={8} w="full" align="center">
            <VStack spacing={2} textAlign="center">
              <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
                Get Started
              </Badge>
              <Heading
                size="xl"
                bgGradient="linear(to-r, purple.400, green.400, blue.400)"
                bgClip="text"
              >
                Configure Your Club
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} maxW="2xl">
                Fill in the details below to set up your custom poker club
              </Text>
            </VStack>

            <Box
              w="full"
              maxW="600px"
              p={8}
              bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'white'}
              borderRadius="2xl"
              border="2px solid"
              borderColor="green.400"
              boxShadow="0 20px 40px rgba(34, 197, 94, 0.15)"
            >
              <VStack spacing={4} as="form">
                {/* Club Name */}
                <FormControl isRequired>
                  <FormLabel fontWeight="bold" fontSize="sm" color={getColor('text.primary', colorMode)}>
                    Club Name
                  </FormLabel>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaUsers} color="green.400" />
                    </InputLeftElement>
                    <Input
                      name="clubName"
                      value={formData.clubName}
                      onChange={handleInputChange}
                      placeholder="Royal Flush Poker Club"
                      bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                      borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
                      _hover={{ borderColor: 'green.400' }}
                      _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' }}
                      pl="2.5rem"
                    />
                  </InputGroup>
                </FormControl>

                {/* Domain */}
                <FormControl>
                  <FormLabel fontWeight="bold" fontSize="sm" color={getColor('text.primary', colorMode)}>
                    Domain
                  </FormLabel>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaGlobe} color="green.400" />
                    </InputLeftElement>
                    <Input
                      name="domain"
                      value={formData.domain}
                      onChange={handleInputChange}
                      placeholder="yourclub.com (or leave blank for subdomain)"
                      bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                      borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
                      _hover={{ borderColor: 'green.400' }}
                      _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' }}
                      pl="2.5rem"
                    />
                  </InputGroup>
                </FormControl>

                {/* DEX Address & Token Address */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} w="100%">
                  <FormControl>
                    <FormLabel fontWeight="bold" fontSize="sm" color={getColor('text.primary', colorMode)}>
                      DEX Address
                    </FormLabel>
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={FaLink} color="green.400" />
                      </InputLeftElement>
                      <Input
                        name="dexAddress"
                        value={formData.dexAddress}
                        onChange={handleInputChange}
                        placeholder="Uniswap V3 Router"
                        bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                        borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
                        _hover={{ borderColor: 'green.400' }}
                        _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' }}
                        pl="2.5rem"
                      />
                    </InputGroup>
                    <Text fontSize="xs" color={getColor('text.secondary', colorMode)} mt={0.5}>
                      Defaults to Uniswap V3 Router
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontWeight="bold" fontSize="sm" color={getColor('text.primary', colorMode)}>
                      Token Address
                    </FormLabel>
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={FaCoins} color="green.400" />
                      </InputLeftElement>
                      <Input
                        name="tokenAddress"
                        value={formData.tokenAddress}
                        onChange={handleInputChange}
                        placeholder="0x1234...5678"
                        bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                        borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
                        _hover={{ borderColor: 'green.400' }}
                        _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' }}
                        pl="2.5rem"
                      />
                    </InputGroup>
                    <Text fontSize="xs" color={getColor('text.secondary', colorMode)} mt={0.5}>
                      USDC by default (ERC-20 format)
                    </Text>
                  </FormControl>
                </SimpleGrid>

                {/* CSV Upload */}
                <FormControl>
                  <FormLabel fontWeight="bold" fontSize="sm" color={getColor('text.primary', colorMode)}>
                    Upload .CSV Stylesheet
                  </FormLabel>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaFileUpload} color="green.400" />
                    </InputLeftElement>
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      p={0}
                      h="auto"
                      pl="2.5rem"
                      py={1.5}
                      bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                      borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
                      _hover={{ borderColor: 'green.400' }}
                      _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' }}
                      sx={{
                        '::file-selector-button': {
                          height: 'auto',
                          padding: '5px 10px',
                          mr: 2,
                          border: 'none',
                          background: colorMode === 'dark' ? 'gray.600' : 'gray.100',
                          fontSize: 'sm',
                          color: colorMode === 'dark' ? 'white' : 'gray.700',
                          borderRadius: 'md',
                        }
                      }}
                    />
                  </InputGroup>
                </FormControl>

                {/* Options */}
                <Box
                  w="full"
                  p={4}
                  bg={colorMode === 'dark' ? 'rgba(72, 187, 120, 0.1)' : 'green.50'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'green.700' : 'green.200'}
                >
                  <Text fontWeight="bold" fontSize="sm" mb={3} color={getColor('text.primary', colorMode)}>
                    Options
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    <FormControl display="flex" alignItems="center">
                      <Switch
                        id="useIpfs"
                        name="useIpfs"
                        isChecked={formData.useIpfs}
                        onChange={handleInputChange}
                        colorScheme="green"
                        mr={2}
                        size="sm"
                      />
                      <FormLabel htmlFor="useIpfs" mb="0" cursor="pointer" fontSize="sm" color={getColor('text.secondary', colorMode)}>
                        Enable IPFS Storage
                      </FormLabel>
                    </FormControl>

                    <FormControl display="flex" alignItems="center">
                      <Switch
                        id="allowNftsAsAvatars"
                        name="allowNftsAsAvatars"
                        isChecked={formData.allowNftsAsAvatars}
                        onChange={handleInputChange}
                        colorScheme="green"
                        mr={2}
                        size="sm"
                      />
                      <FormLabel htmlFor="allowNftsAsAvatars" mb="0" cursor="pointer" fontSize="sm" color={getColor('text.secondary', colorMode)}>
                        Allow NFTs as Avatars
                      </FormLabel>
                    </FormControl>
                  </SimpleGrid>

                  {formData.allowNftsAsAvatars && (
                    <FormControl mt={3}>
                      <FormLabel fontWeight="medium" fontSize="xs" color={getColor('text.primary', colorMode)}>
                        NFT Contract Address
                      </FormLabel>
                      <Input
                        name="nftAddress"
                        value={formData.nftAddress}
                        onChange={handleInputChange}
                        placeholder="0xNFT...Contract"
                        size="sm"
                        bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                        borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
                        _hover={{ borderColor: 'green.400' }}
                        _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' }}
                      />
                    </FormControl>
                  )}
                </Box>

                {/* Node Configuration */}
                <Box
                  w="full"
                  p={4}
                  bg={colorMode === 'dark' ? 'rgba(72, 187, 120, 0.1)' : 'green.50'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'green.700' : 'green.200'}
                >
                  <Text fontWeight="bold" fontSize="sm" mb={3} color={getColor('text.primary', colorMode)}>
                    Node Configuration
                  </Text>
                  <FormControl display="flex" alignItems="center">
                    <Switch
                      id="useOurNode"
                      name="useOurNode"
                      isChecked={formData.useOurNode}
                      onChange={handleInputChange}
                      colorScheme="green"
                      mr={2}
                      size="sm"
                    />
                    <FormLabel htmlFor="useOurNode" mb="0" cursor="pointer" fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      Use Block52 Validator Node
                    </FormLabel>
                  </FormControl>

                  {!formData.useOurNode && (
                    <VStack spacing={3} align="start" w="full" mt={3}>
                      <FormControl>
                        <FormLabel fontWeight="medium" fontSize="xs" color={getColor('text.primary', colorMode)}>
                          Node URL
                        </FormLabel>
                        <InputGroup size="sm">
                          <InputLeftElement pointerEvents="none">
                            <Icon as={FaNodeJs} color="green.400" boxSize={3} />
                          </InputLeftElement>
                          <Input
                            name="nodeUrl"
                            value={formData.nodeUrl}
                            onChange={handleInputChange}
                            placeholder="https://your-validator-node.com"
                            bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                            borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
                            _hover={{ borderColor: 'green.400' }}
                            _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' }}
                            pl="2.5rem"
                          />
                        </InputGroup>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontWeight="medium" fontSize="xs" color={getColor('text.primary', colorMode)}>
                          Node NFT ID
                        </FormLabel>
                        <InputGroup size="sm">
                          <InputLeftElement pointerEvents="none">
                            <Icon as={FaIdCard} color="green.400" boxSize={3} />
                          </InputLeftElement>
                          <Input
                            name="nodeNftId"
                            value={formData.nodeNftId}
                            onChange={handleInputChange}
                            placeholder="NFT-123456"
                            bg={colorMode === 'dark' ? 'gray.700' : 'white'}
                            borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
                            _hover={{ borderColor: 'green.400' }}
                            _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1px #48BB78' }}
                            pl="2.5rem"
                          />
                        </InputGroup>
                      </FormControl>
                    </VStack>
                  )}
                </Box>

                {/* Game Types */}
                <Box
                  w="full"
                  p={4}
                  bg={colorMode === 'dark' ? 'rgba(72, 187, 120, 0.1)' : 'green.50'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'green.700' : 'green.200'}
                >
                  <Text fontWeight="bold" fontSize="sm" mb={3} color={getColor('text.primary', colorMode)}>
                    Game Types
                  </Text>
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl display="flex" alignItems="center">
                      <Switch
                        id="texasHoldem"
                        name="texasHoldem"
                        isChecked={formData.texasHoldem}
                        onChange={handleInputChange}
                        colorScheme="green"
                        mr={2}
                        size="sm"
                      />
                      <FormLabel htmlFor="texasHoldem" mb="0" cursor="pointer" fontSize="sm" color={getColor('text.secondary', colorMode)}>
                        Texas Hold'em
                      </FormLabel>
                    </FormControl>

                    <FormControl display="flex" alignItems="center">
                      <Switch
                        id="omaha"
                        name="omaha"
                        isChecked={formData.omaha}
                        onChange={handleInputChange}
                        colorScheme="green"
                        mr={2}
                        size="sm"
                      />
                      <FormLabel htmlFor="omaha" mb="0" cursor="pointer" fontSize="sm" color={getColor('text.secondary', colorMode)}>
                        Omaha
                      </FormLabel>
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* License Agreement */}
                <FormControl display="flex" alignItems="center">
                  <Switch
                    id="haveAppropriateLicense"
                    name="haveAppropriateLicense"
                    isChecked={formData.haveAppropriateLicense}
                    onChange={handleInputChange}
                    colorScheme="green"
                    mr={3}
                    size="sm"
                  />
                  <FormLabel htmlFor="haveAppropriateLicense" mb="0" cursor="pointer" fontSize="sm" color={getColor('text.secondary', colorMode)}>
                    I have appropriate licenses for operating a poker club
                  </FormLabel>
                </FormControl>

                {/* Submit Button */}
                <Button
                  w="full"
                  size="lg"
                  py={6}
                  bgGradient="linear(to-r, green.400, green.500)"
                  color="white"
                  fontWeight="bold"
                  fontSize="md"
                  _hover={{
                    bgGradient: 'linear(to-r, green.500, green.600)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(34, 197, 94, 0.4)',
                  }}
                  transition="all 0.3s"
                  borderRadius="full"
                  leftIcon={<FaRocket />}
                  onClick={() => handleBuyNow('setup')}
                  isDisabled={!formData.clubName || !formData.haveAppropriateLicense}
                >
                  Buy Now - $15,000 USDC
                </Button>
              </VStack>
            </Box>
          </VStack>

          <Divider borderColor={getColor('border.medium', colorMode)} />

          {/* Partnership Options Section */}
          <VStack spacing={8} w="full">
            <VStack spacing={2} textAlign="center">
              <Badge colorScheme="purple" fontSize="sm" px={3} py={1} borderRadius="full">
                Optional Upgrades
              </Badge>
              <Heading size="xl" color={getColor('text.primary', colorMode)}>
                Partnership & Investment Options
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} maxW="2xl">
                Expand your involvement with additional investment opportunities
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              {/* Read-Only Node Access */}
              <Box
                p={8}
                bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'white'}
                borderRadius="2xl"
                border="1px solid"
                borderColor={getColor('border.medium', colorMode)}
                _hover={{
                  borderColor: 'blue.400',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 40px rgba(59, 130, 246, 0.15)',
                }}
                transition="all 0.3s"
              >
                <VStack align="start" spacing={5}>
                  <Badge colorScheme="blue" fontSize="xs">
                    NETWORK SUPPORTER
                  </Badge>
                  <Heading size="md" color={getColor('text.primary', colorMode)}>
                    Decentralized Read-Only Access
                  </Heading>

                  <HStack align="baseline">
                    <Text fontSize="3xl" fontWeight="bold" color="blue.400">
                      $4,800
                    </Text>
                    <Text color={getColor('text.secondary', colorMode)}>USDC upfront</Text>
                  </HStack>

                  <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                    Equivalent to $200 USDC/month
                  </Text>

                  <Divider />

                  <List spacing={3}>
                    <ListItem fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="blue.400" />
                      <strong>Term:</strong> Two (2) years
                    </ListItem>
                    <ListItem fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="blue.400" />
                      <strong>Access Level:</strong> Node Read-Only
                    </ListItem>
                    <ListItem fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="blue.400" />
                      <strong>Strategic Benefit:</strong> Contributes to network decentralization
                    </ListItem>
                  </List>

                  <Button
                    w="full"
                    variant="outline"
                    borderColor="blue.400"
                    color="blue.400"
                    _hover={{
                      bg: 'blue.400',
                      color: 'white',
                    }}
                    onClick={() => handleBuyNow('readonly')}
                  >
                    Add to Package
                  </Button>
                </VStack>
              </Box>

              {/* Full Node Ownership */}
              <Box
                p={8}
                bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.6)' : 'white'}
                borderRadius="2xl"
                border="2px solid"
                borderColor="orange.400"
                position="relative"
                overflow="hidden"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 40px rgba(251, 146, 60, 0.2)',
                }}
                transition="all 0.3s"
              >
                <Badge
                  position="absolute"
                  top={4}
                  right={4}
                  colorScheme="orange"
                  fontSize="xs"
                >
                  BEST VALUE
                </Badge>

                <VStack align="start" spacing={5}>
                  <Badge colorScheme="orange" fontSize="xs">
                    REVENUE SHARE
                  </Badge>
                  <Heading size="md" color={getColor('text.primary', colorMode)}>
                    Full Node Ownership
                  </Heading>

                  <HStack align="baseline">
                    <Text fontSize="3xl" fontWeight="bold" color="orange.400">
                      $52,000
                    </Text>
                    <Text color={getColor('text.secondary', colorMode)}>USDC upfront</Text>
                  </HStack>

                  <Divider />

                  <List spacing={3}>
                    <ListItem fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="orange.400" />
                      <strong>Term:</strong> In Perpetuity
                    </ListItem>
                    <ListItem fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="orange.400" />
                      <strong>Access Level:</strong> Full Node Ownership
                    </ListItem>
                    <ListItem fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="orange.400" />
                      <strong>Financial Benefit:</strong> Perpetual revenue share of all transaction volume
                    </ListItem>
                    <ListItem fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      <ListIcon as={CheckCircleIcon} color="orange.400" />
                      <strong>Network Benefits:</strong> Decentralization & network resilience
                    </ListItem>
                  </List>

                  <Button
                    w="full"
                    bgGradient="linear(to-r, orange.400, orange.500)"
                    color="white"
                    _hover={{
                      bgGradient: 'linear(to-r, orange.500, orange.600)',
                    }}
                    onClick={() => handleBuyNow('fullnode')}
                  >
                    Add to Package
                  </Button>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>

          {/* See It In Action - Video Section */}
          <VStack spacing={8} w="full">
            <VStack textAlign="center" maxW="800px" mx="auto">
              <Heading
                size="xl"
                bgGradient="linear(to-r, purple.400, green.400, blue.400)"
                bgClip="text"
                mb={3}
              >
                See Instant Branding In Action
              </Heading>
              <Text fontSize="lg" color={getColor('text.secondary', colorMode)}>
                Watch how quickly you can customize your club's look and feel with our powerful branding tools.
              </Text>
            </VStack>

            <Box
              w="100%"
              maxW="900px"
              mx="auto"
              p={6}
              borderRadius="xl"
              bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.5)' : 'white'}
              border="2px solid"
              borderColor="green.400"
            >
              <AspectRatio ratio={16 / 9}>
                <iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/BA7f1ykV01E?si=G8y9MZCW_oW0GqJ3"
                  title="Block52 Instant Branding Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{
                    borderRadius: '8px',
                  }}
                />
              </AspectRatio>
            </Box>
          </VStack>

          {/* Final CTA */}
          <Box
            w="full"
            p={12}
            bgGradient={
              colorMode === 'dark'
                ? 'linear(to-br, green.900, gray.800, purple.900)'
                : 'linear(to-br, green.50, white, purple.50)'
            }
            borderRadius="2xl"
            border="2px solid"
            borderColor="green.400"
            textAlign="center"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              inset="0"
              bgGradient="linear(45deg, rgba(34, 197, 94, 0.1), transparent, rgba(139, 92, 246, 0.1))"
              bgSize="200% 200%"
              animation={`${gradientAnimation} 10s ease infinite`}
            />

            <VStack spacing={6} position="relative" zIndex="1">
              <Heading size="xl" color={getColor('text.primary', colorMode)}>
                Ready to Launch Your Poker Club?
              </Heading>
              <Text fontSize="lg" color={getColor('text.secondary', colorMode)} maxW="2xl">
                Join the Block52 network and become part of the decentralized poker revolution.
                Full setup, branding, and deployment included.
              </Text>
              <Button
                size="lg"
                px={12}
                py={8}
                fontSize="xl"
                fontWeight="bold"
                bgGradient="linear(to-r, green.400, green.500)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, green.500, green.600)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 20px 40px rgba(34, 197, 94, 0.4)',
                }}
                transition="all 0.3s"
                onClick={() => handleBuyNow('setup')}
              >
                Start Now - $15,000 USDC
              </Button>
              <Text fontSize="sm" color={getColor('text.secondary', colorMode)} opacity={0.7}>
                Secure payment via USDC on Ethereum
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />

      {/* USDC Payment Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered size="lg">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
        <ModalContent
          bg={colorMode === 'dark' ? 'gray.800' : 'white'}
          borderRadius="2xl"
          border="1px solid"
          borderColor={getColor('border.medium', colorMode)}
          mx={4}
        >
          <ModalHeader>
            <HStack>
              <Box
                w={10}
                h={10}
                borderRadius="full"
                bg="green.400"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="lg" fontWeight="bold" color="white">$</Text>
              </Box>
              <VStack align="start" spacing={0}>
                <Text>USDC Payment</Text>
                <Text fontSize="sm" fontWeight="normal" color={getColor('text.secondary', colorMode)}>
                  {packages[selectedPackage].name}
                </Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            {paymentStep === 'confirm' && (
              <VStack spacing={6}>
                <Box
                  w="full"
                  p={6}
                  bg={colorMode === 'dark' ? 'gray.700' : 'gray.50'}
                  borderRadius="xl"
                  textAlign="center"
                >
                  <Text fontSize="sm" color={getColor('text.secondary', colorMode)} mb={2}>
                    Amount Due
                  </Text>
                  <Text fontSize="4xl" fontWeight="bold" color="green.400">
                    {packages[selectedPackage].price.toLocaleString()} USDC
                  </Text>
                  <Text fontSize="sm" color={getColor('text.secondary', colorMode)} mt={2}>
                    ≈ ${packages[selectedPackage].price.toLocaleString()} USD
                  </Text>
                </Box>

                <Alert status="info" borderRadius="lg">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Mock Payment Mode</AlertTitle>
                    <AlertDescription fontSize="xs">
                      This is a demo. No actual payment will be processed.
                    </AlertDescription>
                  </Box>
                </Alert>

                <VStack w="full" align="start" spacing={3}>
                  <Text fontWeight="semibold" color={getColor('text.primary', colorMode)}>
                    Payment Details
                  </Text>
                  <HStack w="full" justify="space-between">
                    <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>Network</Text>
                    <Text fontSize="sm" fontWeight="medium">Base (Ethereum L2)</Text>
                  </HStack>
                  <HStack w="full" justify="space-between">
                    <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>Token</Text>
                    <Text fontSize="sm" fontWeight="medium">USDC</Text>
                  </HStack>
                  <HStack w="full" justify="space-between">
                    <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>Est. Gas Fee</Text>
                    <Text fontSize="sm" fontWeight="medium">~$0.01</Text>
                  </HStack>
                </VStack>
              </VStack>
            )}

            {paymentStep === 'processing' && (
              <VStack spacing={6} py={8}>
                <Spinner size="xl" color="green.400" thickness="4px" />
                <VStack spacing={2}>
                  <Text fontWeight="semibold" color={getColor('text.primary', colorMode)}>
                    Processing Payment...
                  </Text>
                  <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                    Confirming transaction on Ethereum
                  </Text>
                </VStack>
              </VStack>
            )}

            {paymentStep === 'success' && (
              <VStack spacing={6} py={4}>
                <Box
                  w={20}
                  h={20}
                  borderRadius="full"
                  bg="green.400"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CheckCircleIcon boxSize={10} color="white" />
                </Box>
                <VStack spacing={2}>
                  <Text fontSize="xl" fontWeight="bold" color={getColor('text.primary', colorMode)}>
                    Payment Successful!
                  </Text>
                  <Text fontSize="sm" color={getColor('text.secondary', colorMode)} textAlign="center">
                    Your {packages[selectedPackage].name} has been confirmed.
                  </Text>
                </VStack>

                <Box
                  w="full"
                  p={4}
                  bg={colorMode === 'dark' ? 'gray.700' : 'green.50'}
                  borderRadius="lg"
                >
                  <VStack spacing={2}>
                    <Text fontSize="xs" color={getColor('text.secondary', colorMode)}>
                      Transaction Hash (Mock)
                    </Text>
                    <Text fontSize="xs" fontFamily="mono" color="green.400">
                      0x7f9e...3d4a...8b2c...1e5f
                    </Text>
                  </VStack>
                </Box>

                <Alert status="success" borderRadius="lg">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Our team will reach out within 24 hours to begin your deployment!
                  </Text>
                </Alert>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            {paymentStep === 'confirm' && (
              <HStack w="full" spacing={4}>
                <Button variant="ghost" onClick={handleCloseModal} flex={1}>
                  Cancel
                </Button>
                <Button
                  flex={2}
                  bgGradient="linear(to-r, green.400, green.500)"
                  color="white"
                  _hover={{
                    bgGradient: 'linear(to-r, green.500, green.600)',
                  }}
                  onClick={handleConfirmPayment}
                >
                  Confirm Payment
                </Button>
              </HStack>
            )}

            {paymentStep === 'success' && (
              <Button w="full" colorScheme="green" onClick={handleCloseModal}>
                Done
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PokerInABox;
